'use client';
import { useCallback, useContext } from 'react';
import { useIsAppDownloading } from '../context/electron-download-context';
import { useInstalledExtensions } from './use-installed-extensions';
import GlobalContext from '../context/global-context';
import useIsIframe from './use-is-iframe';
import useOverwolfApi from './use-overwolf-api';

// Returns a predicate for whether a given appId currently shows the in-progress
// indicator: either an Electron installer is downloading (progress events) or a
// native install was requested and hasn't been reflected as installed yet.
//
// The OW_APP_DL_BTN_SHOW_DOWNLOAD_PROGRESS flag (see next.config.js) only applies
// when the store is embedded in the Overwolf app's iframe shell: there, the flag
// decides whether buttons show the animation. Everywhere else (a normal browser)
// the animation is unaffected by the flag.
export const useDownloadInProgress = (): ((appId: string) => boolean) => {
  const { config } = useContext(GlobalContext);
  const isIframe = useIsIframe();
  const { isAvailable: isInOverwolfClient } = useOverwolfApi();
  const isAppDownloading = useIsAppDownloading();
  const { pendingInstallAppIds } = useInstalledExtensions();

  const inOwAppIframe = isIframe && isInOverwolfClient;
  const showDownloadProgress =
    !inOwAppIframe ||
    (config?.publicRuntimeConfig?.adminParams
      ?.OW_APP_DL_BTN_SHOW_DOWNLOAD_PROGRESS ??
      false);

  return useCallback(
    (appId: string): boolean =>
      showDownloadProgress &&
      !!appId &&
      (isAppDownloading(appId) || pendingInstallAppIds.has(appId)),
    [showDownloadProgress, isAppDownloading, pendingInstallAppIds],
  );
};
